import { ALog, getShareConfig } from 'kuaidong-api'
import { RawLocation, Route } from 'vue-router'
import { Clazz, IStudentNameItem } from 'kuaidong-model'
import { Component, Vue } from 'vue-property-decorator'
import { TitleBar, LoginTeacherService } from 'kuaidong-service'
import {
  UIUtil,
  EnvUtil,
  AppUtil,
  HttpUtil,
  RouterUtil,
  WechatUtil,
  BrowserUtil,
  logger,
  ClazzWrapper,
  StringUtil,
  DateWrapper,
} from 'kuaidong-lib'
import { KDMessageBox } from 'kuaidong-ui'

export * from 'vue-property-decorator'

Component.registerHooks(['beforeRouteEnter', 'beforeRouteLeave', 'beforeRouteUpdate'])

@Component
export class BaseComponent extends Vue {
  loading = false
  loading1 = false
  loading2 = false

  _keepaliveSaveScroll = false // 页面是否需要保存滚动位置，仅keepalive时通过activated方法生效
  _keepaliveScrollValue = 0

  logger = logger
  created() {
    if (this.$route?.meta?.keepAlive) {
      // 废弃了， 见 scrollBehavior
      this._keepaliveSaveScroll = false // keepalive的页面需要保存滚动位置
    }
  }

  // 消息提示
  toast(msg: string) {
    UIUtil.showToast(msg)
  }

  logClick(label: string) {
    this.logEvent('click', label)
  }

  logEvent(action: string, label?: string) {
    this._log(action, label)
  }

  // 日志记录更加详细的内容，最多能带4个参数
  logDetailEvent(event: string, triggerName: string, value: string, subValue?: string) {
    return this._log(event, triggerName, undefined, value, subValue)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _log(eventName: string, triggerName?: string, teacherId?: string, eventId?: string, eventStep?: string) {
    return this.logger.event(this.$route.name!, eventName, triggerName)
  }

  _jsonLog(scene: string, event: string, payload: any = null) {
    return ALog.json(scene, event, payload)
  }

  _logToARMS(event: string, quantity = 1) {
    const __bl = (window as any).__bl
    if (__bl) {
      __bl.sum(event, quantity)
    }
  }

  _getQuery(key: string, defaultValue?: string | number): string {
    let result = this.$route.query[key]
    result = result ? result.toString() : ''
    if (!result && defaultValue) {
      result = `${defaultValue}`
    }
    return result
  }

  _routeReplace(location: RawLocation) {
    return this.$router.replace(location).catch((err) => {
      if (!err.message.includes('Avoided redundant navigation to current location')) {
        // eslint-disable-next-line
        console.error(err)
      }
    })
  }

  _isFromAppShare(): boolean {
    return this._getQuery('share') === 'app'
  }

  _isFromPush(): boolean {
    return this._getQuery('from') === 'push'
  }

  _isFromWeapp(): boolean {
    return this._getQuery('from') === 'weapp'
  }

  _pushPath(path: string) {
    this.$router.push(path)
  }

  _buildFullUrl(rawLocation: RawLocation) {
    const routeData = this.$router.resolve(rawLocation)
    return `${location.href.split('#')[0]}${routeData.href}`
  }

  // 新页面打开， 包括app和web里
  _openNewPage(rawLocation: RawLocation) {
    const url = this._buildFullUrl(rawLocation)
    AppUtil.jumpUrlNewTab(url)
  }

  // 页面回退，如果没有任何历史则回到首页
  _goBack(backLevel = -1) {
    // 当前页也会在history.length里，所以大于, 暂时没有考虑backLevel为正值的情况
    if (window.history.length > Math.abs(backLevel)) {
      console.log('in base ' + backLevel)
      this.$router.go(backLevel)
    } else {
      // 没有则回到首页
      this.$router.push('/')
    }
  }

  _student_app_name(item: IStudentNameItem) {
    // app里对应的名字
    return item.note_name || item.full_name || item.nick_name
  }

  _show_student_name(item: IStudentNameItem) {
    // 最常用的显示学生名字
    return item.note_name || this._student_app_name(item) || '-'
  }

  _student_note_name(item: IStudentNameItem) {
    // 老师给学生标注的名字
    return item.note_name || '-'
  }

  _student_real_name(item: IStudentNameItem) {
    // 老师给学生标注的名字
    return item.note_name || item.full_name || '-'
  }

  _show_clazz_name(item: Clazz) {
    // 显示班级名字
    return new ClazzWrapper(item).name
  }

  _show_fulltime(theTime: string | Date) {
    return new DateWrapper(theTime).showFullStr
  }

  _show_datetime_short(theTime: string | Date) {
    return new DateWrapper(theTime).showStr
  }

  // 执行异步任务，增加loading效果
  async _LoadingAsyncTask(func: () => Promise<void>) {
    UIUtil.showLoading()
    try {
      this.loading = true // 设上对应状态
      await func()
    } finally {
      this.loading = false
      UIUtil.hideLoading()
    }
  }

  _checkRoleAndExecute(roleAccess: boolean, func: () => void, toastMsg = '暂无权限，请联系学校管理者操作！') {
    // 判断是否有权限，有权限则执行callback, 否则提示
    // eg this.$p.m.teacherMgt.create
    if (roleAccess) {
      func()
      return true
    } else {
      this.toast(toastMsg)
      return false
    }
  }

  activated() {
    // component的activated和deactivated生命周期, 使用keepalive的时候方法会被执行
    // 页面的activated和deactivated 也会被调用，这里会先被调用
    if (this._keepaliveSaveScroll && this._keepaliveScrollValue) {
      window.scroll({ top: this._keepaliveScrollValue })
    }
  }

  beforeRouteLeave(to: Route, from: Route, next: () => void) {
    // 离开页面前执行一些操作
    if (this && this._keepaliveSaveScroll) {
      // 使用deactivated的时候，页面scroll值读不到，只用通过beforeRouteLeave来保存
      this._keepaliveScrollValue = window.scrollY
    }
    next()
  }

  _toFixedNum(num: number, decimal = 2) {
    return StringUtil.toFixed(num, decimal)
  }

  /**
   * 跳转到特定的分享页面，分享页面内容根据配置来, 通过配置id跳转
   * 测试环境配置地址: https://www-test.tiantiantiaosheng.com/admin/#/setting/configs/512905668714303172
   * @param shareConfigId 分享配置id
   */
  _gotoSharePage(shareConfigId: string) {
    this.$router.push({
      path: 'common-share',
      query: {
        id: shareConfigId,
      },
    })
  }
}

@Component
export class BasePage extends BaseComponent {
  pageTitle: string | null = null

  _wxShareTitle = ''
  _wxShareDesc = ''
  _wxShareImg = ''
  // Property "_isUnderApp" must be accessed with "$data._isUnderApp" because properties starting with "$" or "_" are not proxied in the Vue instance to prevent conflicts with Vue internals. See: https://vuejs.org/v2/api/#data
  _isUnderApp = false

  created() {
    if (EnvUtil.isApph5() || EnvUtil.isCampus() || EnvUtil.isAgency()) {
      this._setTitleBar()
      this._setTitle(this.getPageTitle())
    }

    if (EnvUtil.isApph5()) {
      this._wxShareTitle = '天天跳绳'
      this._wxShareDesc = '随时随地跳绳，全自动AI计数，跳绳入门训练神器'
      this._wxShareImg =
        'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-06-08/dc030dc2-eb3d-480a-a1b0-2586176fb50a/e284643e-a11f-421f-8f2b-0b83f0308a0a.png?x-oss-process=image/resize,h_68,m_lfit'
    } else {
      this._wxShareTitle = EnvUtil.appNameZHCN()
      this._wxShareDesc = EnvUtil.appNameDesc()
      this._wxShareImg =
        'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-10-20/5284ef74-a4dc-44cc-9575-85405579c81d/a3c271b0-4b6a-455c-ad6a-febb5e1ea497.png?x-oss-process=image/resize,h_68,m_lfit'
    }
    if (this.$route.query.app === 'tzy') {
      BrowserUtil.updateTitle('体智云')
      this._wxShareTitle = '体智云'
      this._wxShareDesc = '校园体育智慧云平台'
      this._wxShareImg =
        'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-10-20/5284ef74-a4dc-44cc-9575-85405579c81d/a3c271b0-4b6a-455c-ad6a-febb5e1ea497.png?x-oss-process=image/resize,h_68,m_lfit'
    }

    AppUtil.isUnderApp().then((v) => (this._isUnderApp = v))
  }

  mounted() {
    if (!this.$route.meta?.keepAlive) {
      BrowserUtil.fixIosWhiteScreenBug()
    }

    this._initWechatShare()
    this._logPageVisit()
  }

  // keepalive的组件或页面生命周期
  activated() {
    this._setTitle(this.getPageTitle())
  }

  async _getQD() {
    const teacherId = await LoginTeacherService.safeGetTeacherId()

    const key = `__${teacherId}_visite_page_qd__`

    let qd = (this.$route.query?.qd || '') as string
    if (qd) {
      sessionStorage.setItem(key, qd)
    } else {
      qd = sessionStorage.getItem(key) || ''
    }

    return qd
  }

  // 更新标题
  _setTitle(title: string) {
    BrowserUtil.updateTitle(title)

    if (EnvUtil.isCampus() || EnvUtil.isAgency()) {
      TitleBar.setTitle(title)
    }
  }

  _forceAuth() {
    console.log('forceAuth', this.$route, this.$router)
    RouterUtil.forceAuth(this.$route, this.$router)
  }

  async _logPageVisit() {
    const qd = await this._getQD()

    if (EnvUtil.isCampus() || EnvUtil.isSaas() || EnvUtil.isApph5()) {
      // Use route name as the event category to group all events for single page.
      this.logger.event(this.$route.name!, 'visitPage', '访问页面', 1, { qd })
    }
  }

  _setTitleBar() {
    TitleBar.clearBackPath()

    if (this._isFromPush()) {
      TitleBar.disableBack()
    }
  }

  _disableTitleBar() {
    TitleBar.disableBack()
  }

  async _initWechatShare(linkUrl?: string) {
    if (BrowserUtil.isWechat()) {
      const wx = await WechatUtil.getInstance()
      // console.log(`init set config ${wx} ${this._wxShareTitle} ${this._wxShareDesc} ${this._wxShareImg}`)
      wx.setShareConfig(this._wxShareTitle, this._wxShareDesc, this._wxShareImg, linkUrl)
    }
  }

  async _setWechatShareFromConfig(source_id: string) {
    try {
      const config = await getShareConfig(source_id)
      const configItems = (config?.items || []).map((item: any, index: number) => ({
        idx: index,
        ...item,
      }))
      const wxShareItems = configItems.filter((item: any) => item.item_type === 'wechat')
      if (wxShareItems.length > 0) {
        const item = wxShareItems[0]
        await this._updateWechatShare(
          item.item_config_wechat.title,
          item.item_config_wechat.share_content,
          item.item_config_wechat.img_url
        )
      }
    } catch (e) {
      console.log(e)
    }
  }

  async _updateWechatShare(title?: string, desc?: string, imgUrl?: string, linkUrl?: string) {
    if (title) {
      this._wxShareTitle = title
    }
    if (desc) {
      this._wxShareDesc = desc
    }
    if (imgUrl) {
      this._wxShareImg = imgUrl
    }
    // console.log(
    //   `init wechat share ${BrowserUtil.isWechat()} ${this._wxShareTitle} ${this._wxShareDesc} ${this._wxShareImg}`
    // )
    return this._initWechatShare(linkUrl)
  }

  get backLevel() {
    return this.$route.query.backLevel ? Number(this.$route.query.backLevel) : -1
  }

  private getPageTitle() {
    return this.pageTitle || this.$route.meta?.title || TitleBar.defaultTitle
  }

  async _share(_url = location.href) {
    if (!(await AppUtil.isUnderApp())) {
      KDMessageBox.alert('请通过微信或者APP内分享！')
    }

    AppUtil.shareLink(_url, this._wxShareTitle, this._wxShareDesc, await HttpUtil.imageProxy(this._wxShareImg))
  }

  _matchRoute(raw: RawLocation) {
    const route = this.$router.match(raw)
    // 需要过滤掉默认匹配
    if (route && route.name !== 'page_not_found') {
      console.log(route)
      return route
    } else {
      return null
    }
  }
}
